import fetchStorefrontAccessToken from "./storeFrontAccessToken";
import { updateDiscountCodes } from "./mutations/updateDiscountCodes";
import { addLinesToStorefrontCart } from "./mutations/addLinesToCart";
import { createCart } from "./mutations/createCart";
import { updateGiftCardCodes } from "./mutations/updateGiftCardCodes";
class CheckoutDiscountCode {
  constructor(container) {
    this.shopLocale = Theme.settings.shop_locale;
    this.storeHandle = Theme.settings.store_handle;
    const { endpoint, token } = fetchStorefrontAccessToken(this.shopLocale, this.storeHandle);
    if(!endpoint || !token){
      console.error("❌ No Storefront endpoint or token found:", { locale: this.shopLocale, store: this.storeHandle });
      return;
    }
    this.appliedCodes = {
      discountCode: {
        code: null,
        amount: null
      },
      giftCardCode: {
        code: null,
        amount: null
      }
    }

    this.storefrontAccessToken = token;
    this.endpoint = endpoint;
    this.cartId = null;
    this.discountErrorStates = {
      DISCOUNT_NOT_FOUND: "discount-not-found",
      DISCOUNT_USAGE_LIMIT_REACHED: "discount-usage-limit-reached",
      DISCOUNT_ALREADY_APPLIED: "discount-already-applied",
      DISCOUNT_NOT_APPLICABLE: "discount-not-applicable",
      DISCOUNT_NO_ENTITLED_LINE_ITEMS: "discount-no-entitled-line-items",
      DISCOUNT_CURRENTLY_INACTIVE: "discount-currently-inactive",
      GIFT_CARD_NOT_FOUND: "gift-card-not-found",
      GIFT_CARD_ALREADY_APPLIED: "gift-card-already-applied"
    }
    this.checkoutCard = container.querySelector(".checkout-order__card[data-is-main='true']");
    if(!this.checkoutCard){
      return;
    }
    this.discountWrapper = this.checkoutCard.querySelector(".checkout-discount");
    this.discountCodeInput = this.checkoutCard.querySelector("#checkout-discount");
    this.discountCodeButton = this.checkoutCard.querySelector(".checkout-discount__button");
    if(!this.discountCodeInput || !this.discountCodeButton || !this.discountWrapper){
      return;
    }
    this.isDiscountLocked = false;
    this.originalPlaceholder = this.discountCodeInput.placeholder || '';
    this.init();
  }

  async init() {
    this.checkForAutomaticDiscounts();
    try {
      await this.initStorefrontCart();
    } catch (error) {
      // Storefront cart init can fail (ad/privacy blockers, network) — checkout must still work.
      console.error("❌ Storefront cart init failed:", error);
    }
    document.addEventListener('checkout:remove-all-codes', () => {
      this.removeAllCodes();
    });
    this.discountCodeInput.addEventListener("input", () => {
      this.setDiscountButtonStatus();
      if(this.discountCodeInput.value.length === 0){
        this.removeErrorMessage();
      }
    });
    this.applyAllCodesEvent();
    await this.checkForPreAppliedDiscounts();
  }

  async checkForPreAppliedDiscounts() {
    // 1. Check URL parameter
    const urlParams = new URLSearchParams(window.location.search);
    const urlDiscount = urlParams.get('discount');

    if (urlDiscount) {
      const { success } = await this.applyDiscountCode(urlDiscount);
      if (success) {
        window.checkout.manualDiscountCode = urlDiscount;
        this.showDiscount(urlDiscount);
        this.calculateDiscountAmount();
        this.lockDiscountInput();
        return;
      }
    }

    // 2. Check cart-page discount codes (from data attribute)
    const cartDiscountCodes = this.discountWrapper?.dataset?.cartDiscountCodes;
    if (cartDiscountCodes) {
      const codes = cartDiscountCodes.split(',').map(c => c.trim()).filter(Boolean);
      for (const code of codes) {
        const { success } = await this.applyDiscountCode(code);
        if (success) {
          window.checkout.manualDiscountCode = code;
          this.showDiscount(code);
          this.calculateDiscountAmount();
          this.lockDiscountInput();
          return;
        }
      }
    }

    // 3. Check if automatic discounts were found (already handled by checkForAutomaticDiscounts)
    if (window.checkout.automaticDiscountCode) {
      this.lockDiscountInput();
    }
  }

  checkForAutomaticDiscounts() {
    // Free-shipping automatic discounts are identified in Liquid (by title token, see
    // `checkout-order-overview.liquid`) and exposed on the main card. Only these are treated
    // like a manual code — pill + savings line + locked input. Product-price automatic
    // discounts (e.g. 2-for-89) are deliberately NOT surfaced here: they keep their inline
    // "Rabatkode: {title}" tag, and their reduction already lives in cart.total_price.
    const fsAmount = parseFloat(this.checkoutCard.dataset.freeShippingDiscountAmount) || 0;
    const fsTitles = (this.checkoutCard.dataset.freeShippingDiscountTitles || '')
      .split(',')
      .map(t => t.trim())
      .filter(Boolean);

    if (fsAmount <= 0 || fsTitles.length === 0) {
      return;
    }

    window.checkout.automaticDiscountCode = fsTitles.join(', ');
    window.checkout.automaticDiscountCodes = fsTitles;
    // `fsAmount` is in cents (Liquid allocation.amount). It is RELOCATED to the shipping line
    // (delivery list + order summary), NOT shown as a product savings row — so it is stored in
    // its own field and deliberately NOT in `automaticDiscountAmount` (which drives the savings row).
    window.checkout.freeShippingDiscountAmount = fsAmount;

    // Re-render the order overview (and delivery prices) with the free-shipping amount applied.
    this.updateCombinedDiscountAmount();

    // Render each free-shipping discount as a pill (showAutomaticDiscount omits the remove button).
    fsTitles.forEach(title => {
      this.showAutomaticDiscount(title);
    });
  }


  removeAllCodes() {
    if(window.checkout.manualDiscountCode){
      const discountCode = this.discountWrapper.querySelector(".discount-code[data-code='" + window.checkout.manualDiscountCode + "']");
      if(discountCode){
        this.removeCode(discountCode, "discount");
      }
    }
    if(window.checkout.giftCardCode){
      const giftCardCode = this.discountWrapper.querySelector(".gift-card-code[data-code='" + window.checkout.giftCardCode + "']");
      if(giftCardCode){
        this.removeCode(giftCardCode, "giftCard");
      }
    }
  }

   applyAllCodesEvent() {
    this.discountCodeButton.addEventListener("click", async () => {
      let giftCardSuccess = false;
      let giftCardErrorCode = "";
      let giftCardMessage = "";
      let discountSuccess = false;
      let discountMessage = "";
      let discountErrorCode = "";

      // If discount is already locked, skip discount attempt — only try gift card
      if (!this.isDiscountLocked) {
        const discountResult = await this.applyDiscountCode(this.discountCodeInput.value);
        discountSuccess = discountResult.success;
        discountMessage = discountResult.message;
        discountErrorCode = discountResult.errorCode;
      }

      if(!discountSuccess){
        const giftCardResult = await this.applyGiftCardCode(this.discountCodeInput.value);
        giftCardSuccess = giftCardResult.success;
        giftCardErrorCode = giftCardResult.errorCode;
        giftCardMessage = giftCardResult.message;
      }

      if(!discountSuccess && !giftCardSuccess) {
        const errorCode = this.isDiscountLocked ? (giftCardErrorCode || 'DISCOUNT_ALREADY_APPLIED') : (discountErrorCode || giftCardErrorCode);
        const errorMessage = this.isDiscountLocked ? (giftCardMessage || 'Discount already applied') : (discountMessage || giftCardMessage);
        this.showErrorMessage(errorCode, errorMessage);
        return;
      } else {
        this.removeErrorMessage();
      }

      if(discountSuccess){
        this.showDiscount(this.discountCodeInput.value);
        this.lockDiscountInput();
      }
      if(giftCardSuccess){
       this.showGiftCard(this.discountCodeInput.value);
      }
      this.calculateDiscountAmount()
      this.discountCodeInput.value = "";
    });
  }

  setDiscountButtonStatus() {
    if (this.discountCodeInput.value.length < 1) {
      this.discountCodeButton.classList.remove("active");
    } else {
      this.discountCodeButton.classList.add("active");
    }
  }

  async initStorefrontCart() {
    this.cartId = await createCart(this.endpoint, this.storefrontAccessToken);
    const result = await this.addProductToCartLines();
    if(!result){
      console.error("❌ Failed to add product to Storefront cart.");
      return;
    }
  }
  
  async addProductToCartLines() {
    if (!this.cartId) {
      console.error("❌ No Storefront cart ID found in localStorage.");
      return;
    }
    const classicCart = await this.fetchClassicCart();
    const storefrontLines = this.convertClassicCartToStorefrontLines(classicCart);
    const result = await addLinesToStorefrontCart(this.endpoint, this.storefrontAccessToken, this.cartId, storefrontLines);
    return result;
  }

  async fetchClassicCart() {
    try {
      const cartResponse = await fetch('/cart.js');
      return await cartResponse.json();
    } catch (err) {
      console.error("❌ Failed to fetch classic cart:", err);
      throw err;
    }
  }

  convertClassicCartToStorefrontLines(classicCart) {
    if (!classicCart.items || classicCart.items.length === 0) {
      return [];
    }

    return classicCart.items.map(item => ({
      merchandiseId: `gid://shopify/ProductVariant/${item.variant_id}`,
      quantity: item.quantity
    }));
  }

  async ensureStorefrontCart() {
    if (this.cartId) return true;
    try {
      await this.initStorefrontCart();
    } catch (error) {
      console.error("❌ Storefront cart retry failed:", error);
    }
    return !!this.cartId;
  }

  async applyDiscountCode(discountCode) {
    if(this.appliedCodes.discountCode.code !== null && this.appliedCodes.discountCode.amount !== null) {
      return { success: false, message: "Discount already applied", errorCode: "DISCOUNT_ALREADY_APPLIED" };
    };
    const cartReady = await this.ensureStorefrontCart();
    if(!cartReady){
      return { success: false, message: "Discount not applied", errorCode: "DISCOUNT_NOT_FOUND" };
    }
    this.discountCodeButton.classList.remove("active");

    // If this code is in `data-cart-discount-codes`, it was already applied to the live Shopify
    // cart (e.g. via the cart page) and therefore cart.total_price already reflects it.
    // If not, the code only lives in the Storefront API cart and cart.total_price is unchanged —
    // the display math in setupOrderOverview needs to know which case we're in.
    // Compare case-insensitively because Shopify codes are case-insensitive at apply time but
    // `cart_level_discount_applications | map: 'title'` returns the canonical case.
    const enteredCode = (discountCode || '').toLowerCase();
    const cartDiscountCodes = (this.discountWrapper?.dataset?.cartDiscountCodes || '')
      .split(',').map(c => c.trim().toLowerCase()).filter(Boolean);
    const isInLiveCartTotal = cartDiscountCodes.includes(enteredCode);

    // Just apply the manual discount code - Shopify will keep automatic discounts
    const updateDiscountCodesResponse = await updateDiscountCodes(this.endpoint, this.storefrontAccessToken, this.cartId, discountCode);
    if(!updateDiscountCodesResponse){
      return { success: false, message: "Discount not applied", errorCode: "DISCOUNT_NOT_FOUND" }
    }
    const { success, message, errorCode } = await this.handleDiscountCodeApplicationResponse(updateDiscountCodesResponse, isInLiveCartTotal);
    return { success: success, message: message, errorCode: errorCode };
  }

  async applyGiftCardCode(giftCardCode) {
    if(this.appliedCodes.giftCardCode.code !== null && this.appliedCodes.giftCardCode.amount !== null) {
      return { success: false, message: "Gift card already applied", errorCode: "GIFT_CARD_ALREADY_APPLIED" };
    };
    // Same lazy retry as applyDiscountCode — needed here too because when an automatic discount
    // locks the input, only the gift-card path runs and would otherwise hit a null cartId.
    const cartReady = await this.ensureStorefrontCart();
    if(!cartReady){
      return { success: false, message: "Gift card not applicable.", errorCode: "GIFT_CARD_NOT_FOUND" };
    }
    this.discountCodeButton.classList.remove("active");
    const updateGiftCardCodesResponse = await updateGiftCardCodes(this.endpoint, this.storefrontAccessToken, this.cartId, giftCardCode);
    const { success, message, errorCode = "" } = await this.handleGiftCardCodeApplicationResponse(giftCardCode, updateGiftCardCodesResponse);
    return { success: success, message: message, errorCode: errorCode };
  }

  async handleGiftCardCodeApplicationResponse(giftCardCode, response) {
    const update = response.data?.cartGiftCardCodesUpdate;
    if (update?.cart?.appliedGiftCards.length > 0) {
      let giftCardAmount = update?.cart?.appliedGiftCards?.[0]?.amountUsed?.amount;
      if(giftCardAmount){
        giftCardAmount = parseFloat(giftCardAmount) * 100;
      }
      if(giftCardAmount && giftCardCode){
        window.checkout.giftCardCode = giftCardCode;
        window.checkout.giftCardAmount = giftCardAmount;
      }
      this.appliedCodes.giftCardCode.code =   giftCardCode;
      this.appliedCodes.giftCardCode.amount = giftCardAmount;
      return { success: true, message: `Gift card "${giftCardCode}" applied!` };
    } else {
      const errorCode = update?.warnings?.[0]?.code || "GIFT_CARD_NOT_FOUND";
      const errorMsg = update?.warnings?.[0]?.message || "Gift card not applicable.";
      this.showErrorMessage(errorCode, errorMsg);
      return { success: false, message: errorMsg, errorCode };
    }
  }

  async handleDiscountCodeApplicationResponse(response, isInLiveCartTotal = false) {
    const update = response.data?.cartDiscountCodesUpdate;
    if (update?.cart?.discountCodes?.[0]?.applicable) {
      const discountCodeName = update?.cart?.discountCodes?.[0]?.code;
      const discountCost = update?.cart?.cost;
      let appliedDiscountAmount = 0;
      const appliedGiftCards = update?.cart?.appliedGiftCards;
      if(appliedGiftCards.length > 0){
        const appliedGiftCardAmountRaw = appliedGiftCards[0].amountUsed.amount;
        if(appliedGiftCardAmountRaw) appliedDiscountAmount = parseFloat(appliedGiftCardAmountRaw) * 100;
      }
      const oldAmount = discountCost?.checkoutChargeAmount?.amount;
      const newAmount = discountCost?.totalAmount?.amount;
      let discountAmount = (parseFloat(oldAmount) - parseFloat(newAmount)) * 100;
      discountAmount = discountAmount - appliedDiscountAmount;
      if(discountCodeName && appliedDiscountAmount == 0){
        window.checkout.manualDiscountCode = discountCodeName;
        window.checkout.discountAmount = discountAmount;
        if(discountAmount == 0){
          window.checkout.isFreeDeliveryApplied = true;
        }
      } else if(discountCodeName && appliedDiscountAmount > 0) {
        window.checkout.manualDiscountCode = discountCodeName;
        window.checkout.discountAmount = discountAmount;
        if(discountAmount == 0){
          window.checkout.isFreeDeliveryApplied = true;
        }
      }
      this.appliedCodes.discountCode.code = discountCodeName;
      this.appliedCodes.discountCode.amount = discountAmount;
      this.appliedCodes.discountCode.alreadyInLiveCartTotal = isInLiveCartTotal;
      return { success: true, message: `Discount "${discountCodeName}" applied!` };
    } else {
      const errorCode = update?.warnings?.[0]?.code || "DISCOUNT_NOT_FOUND";
      const errorMsg = update?.warnings?.[0]?.message || "Discount not applicable.";
      return { success: false, message: errorMsg, errorCode };
    }
  }

  showDiscount(discountCodeName) {
    const discountCodesGroup = this.discountWrapper.querySelector(".discount-codes__group");
    if(!discountCodesGroup) return;
    const discountCodeTemplate = discountCodesGroup.querySelector("template");
    if(!discountCodeTemplate) return;
    const discountCodeFragment = discountCodeTemplate.content.cloneNode(true);
    if(!discountCodeFragment) return;

    const discountCode = discountCodeFragment.firstElementChild;
    discountCodesGroup.appendChild(discountCode);
    discountCode.querySelector(".discount-code__body").innerText = discountCodeName;
    discountCode.querySelector(".discount-code__close").addEventListener("click", () => {
      this.removeCode(discountCode, "discount");
    });
    discountCode.setAttribute("data-code", discountCodeName);
  }

  showAutomaticDiscount(discountCodeName) {
    const discountCodesGroup = this.discountWrapper.querySelector(".discount-codes__group");
    if(!discountCodesGroup) return;
    const discountCodeTemplate = discountCodesGroup.querySelector("template");
    if(!discountCodeTemplate) return;
    const discountCodeFragment = discountCodeTemplate.content.cloneNode(true);
    if(!discountCodeFragment) return;

    const discountCode = discountCodeFragment.firstElementChild;
    discountCodesGroup.appendChild(discountCode);
    discountCode.querySelector(".discount-code__body").innerText = discountCodeName;

    // Remove the close button for automatic discounts since they can't be removed manually
    const closeButton = discountCode.querySelector(".discount-code__close");
    if(closeButton) {
      closeButton.remove();
    }

    discountCode.setAttribute("data-code", discountCodeName);
    discountCode.setAttribute("data-automatic-discount", "true");
  }

  showGiftCard(giftCardCodeName) {
    const giftCardCodesGroup = this.discountWrapper.querySelector(".gift-card-codes__group");
    if(!giftCardCodesGroup) return;
    const giftCardCodeTemplate = giftCardCodesGroup.querySelector("template");
    if(!giftCardCodeTemplate) return;
    const giftCardCodeFragment = giftCardCodeTemplate.content.cloneNode(true);
    if(!giftCardCodeFragment) return;
    const giftCardCode = giftCardCodeFragment.firstElementChild;
    if(!giftCardCode) return;

    giftCardCodesGroup.appendChild(giftCardCode);
    giftCardCode.querySelector(".gift-card-code__body").innerText = giftCardCodeName;
    giftCardCode.querySelector(".gift-card-code__close").addEventListener("click", () => {
      this.removeCode(giftCardCode, "giftCard");
    });
    giftCardCode.setAttribute("data-code", giftCardCodeName);
  }

  async removeCode(code, type) {
    if(!type || !code) return;
    if(window.checkout){
      if(type === "discount"){
        window.checkout.manualDiscountCode = null;
        window.checkout.discountAmount = null;
        this.appliedCodes.discountCode.code = null;
        this.appliedCodes.discountCode.amount = null;
        // Keep free delivery if a subscription variant provides it independently of the code.
        window.checkout.isFreeDeliveryApplied = window.checkout.subscriptionFreeShipping || false;

        // Remove the manual discount from Storefront cart - automatic discounts will remain
        await updateDiscountCodes(this.endpoint, this.storefrontAccessToken, this.cartId, []);
      } else if(type === "giftCard"){
        window.checkout.giftCardCode = null;
        window.checkout.giftCardAmount = null;
        this.appliedCodes.giftCardCode.code = null;
        this.appliedCodes.giftCardCode.amount = null;
      }
    }
    this.updateCombinedDiscountAmount();
    code.remove();

    // Unlock input if no discounts remain
    const hasRemainingDiscount = this.appliedCodes.discountCode.code !== null || window.checkout.automaticDiscountCode;
    if (!hasRemainingDiscount) {
      this.unlockDiscountInput();
    }
  }

  showErrorMessage(type, message) {
    if(!this.discountErrorStates[type]) return;
    const errorTypeClass = this.discountErrorStates[type];
    const errorContainer = this.discountWrapper.querySelector(".discount-error");
    errorContainer.classList.remove("d-none");

    errorContainer.innerText = message;
    errorContainer.classList.add(errorTypeClass);

  }
  removeErrorMessage(){
    const errorContainer = this.discountWrapper.querySelector(".discount-error");
    errorContainer.classList.add("d-none");
    errorContainer.classList.remove("discount-not-found");
    errorContainer.classList.remove("discount-usage-limit-reached");
    errorContainer.classList.remove("discount-already-applied");
    errorContainer.classList.remove("discount-no-entitled-line-items");
    errorContainer.innerText = "";
  }
    
  updateCombinedDiscountAmount() {
    const manualDiscountAmount = this.appliedCodes.discountCode.amount || 0;
    const giftCardAmount = this.appliedCodes.giftCardCode.amount || 0;
    // We no longer add automaticDiscountAmount here because the
    // UI (index.js) adds it separately.

    const manualTotal = manualDiscountAmount + giftCardAmount;

    // The portion of `manualDiscountAmount` already reflected in cart.total_price
    // (i.e. codes that were applied to the live cart, not just the Storefront API cart).
    // The display layer needs this to differentiate "add back to show original price"
    // (already-in-cart codes) from "subtract from total" (just-applied-on-checkout codes).
    const manualInLiveCartTotal = this.appliedCodes.discountCode.alreadyInLiveCartTotal
      ? manualDiscountAmount
      : 0;

    // This now represents only "Manual" additions
    window.checkout.discountAmountCombined = manualTotal;
    window.checkout.discountAmountInLiveCartTotal = manualInLiveCartTotal;

    document.dispatchEvent(new CustomEvent('checkout:updated', {
      detail: {
        discountAmount: manualTotal,
        discountAmountInLiveCartTotal: manualInLiveCartTotal
      }
    }));
  }

  calculateDiscountAmount() {
    // Check if the applied manual discount resulted in 0 (Free Shipping)
    if(this.appliedCodes.discountCode.code && this.appliedCodes.discountCode.amount === 0){
        document.dispatchEvent(new CustomEvent('free-delivery:applied', { 
          detail: { freeDelivery: true } 
        }));
    }
    this.updateCombinedDiscountAmount();
  }
  
  lockDiscountInput() {
    this.isDiscountLocked = true;
    this.discountCodeInput.value = '';
    this.discountCodeInput.placeholder = window.theme?.strings?.checkout_discount_already_applied || window.Theme?.strings?.checkout_discount_already_applied || 'Rabatkode allerede anvendt';
  }

  unlockDiscountInput() {
    this.isDiscountLocked = false;
    this.discountCodeInput.value = '';
    this.discountCodeInput.placeholder = this.originalPlaceholder;
  }
}

export default CheckoutDiscountCode;